import axiosClient from './axiosClient';

/** A platform tenant that exists on the platform but not yet in config. */
export interface SyncCandidate {
  tenant_id: number;
  tenant_code: string;
  tenant_name: string;
  sponsor_id: number | null;
}

export interface SyncEntityResult {
  entity: string;
  platformRows: number;
  importedRows: number;
}

export interface SyncExclusion {
  entity: string;
  reason: string;
}

export interface FidelityWarning {
  category: string;
  code: string;
  name?: string;
  status: 'new' | 'changed' | 'deleted';
  fieldChanges?: Array<{ field: string; before: unknown; after: unknown }>;
}

export interface SyncVerification {
  rePublishWouldBeNoop: boolean | null;
  categories: Record<string, { new: number; changed: number; unchanged: number; deleted: number; total: number }>;
  fidelityWarnings: FidelityWarning[];
  error?: string;
}

export interface SyncResult {
  tenantCode: string;
  platformTenant: { tenant_id: number; tenant_name: string } | null;
  dryRun: boolean;
  entities: SyncEntityResult[];
  excluded: SyncExclusion[];
  warnings: string[];
  /** Present only on apply. */
  verification?: SyncVerification;
}

export const syncPlatformApi = {
  /** Platform tenants not yet under client-portal (config) management. */
  candidates: async (): Promise<SyncCandidate[]> => {
    const { data } = await axiosClient.get('/api/sync-from-platform/candidates');
    return data.candidates as SyncCandidate[];
  },

  /** Dry-run: runs the full import then rolls back. Returns would-import counts. */
  preview: async (tenantCode: string): Promise<SyncResult> => {
    const { data } = await axiosClient.post(`/api/sync-from-platform/${tenantCode}/preview`);
    return data as SyncResult;
  },

  /** Commit the import, then return the informational no-op verification. */
  apply: async (tenantCode: string): Promise<SyncResult> => {
    const { data } = await axiosClient.post(`/api/sync-from-platform/${tenantCode}/apply`);
    return data as SyncResult;
  },
};
